'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { motion, useScroll, useSpring } from 'motion/react';
import type { Variants } from 'motion/react';
import { RiInformationLine } from '@remixicon/react';
import { useLocale, useTranslations } from 'next-intl';
import {
  LOCALE_META,
  TRANSLATED_LOCALES,
  type LegalBlock,
  type LegalDocDetail,
} from '@mentorivo/shared';

import { useInlineSign } from '@/i18n/direction';
import { Link } from '@/i18n/navigation';
import { formatDate } from '@/lib/format';
import { dur, ease, spring, springFade, stagger } from '@/lib/motion';

/**
 * Yasal metnin okuma yüzeyi — başlık, künye, belgeler şeridi, gövde, içindekiler.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * 🔴 Deri `.art-*` — YENİ bir okuma yüzeyi yazılmadı
 * ═══════════════════════════════════════════════════════════════════════════
 * Çerçeve, tipografi, içindekiler sütunu, okuma çubuğu ve blok aralıkları
 * blog yazı sayfasının (`blog-article.tsx`) sınıflarını kullanıyor. Sebep bu
 * depodaki kural: yapılmış bir yüzey varsa ikincisi yazılmaz.
 *
 * Aynı yüzey olmaları ayrıca DOĞRU: ikisi de uzun metin, ikisi de aynı
 * ızgarada, ikisi de aynı ölçüyü (measure) ve satır aralığını istiyor. İki
 * ayrı deri, sitenin iki farklı yerde farklı okuma ritmi sunması demekti.
 *
 * ⚠️ Bunun bedeli bir bağ: `.art-*` kuralları değişirse yasal sayfalar da
 * değişir. Bilinçli — ama `globals.css` → "Yazı sayfası" bölümüne dokunan
 * kişinin bilmesi gerekiyor, o yüzden orada da not var.
 *
 * Yalnızca BURAYA ait iki şey için yeni sınıf var (`globals.css` → "Yasal
 * metinler"): belgeler arası şerit ve tablo bloğu. İkisinin de blogda
 * karşılığı yok.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Neden geri bağlantısı yok, belgeler şeridi var
 * ═══════════════════════════════════════════════════════════════════════════
 * Blog yazısının başında "Tüm yazılar" var çünkü bir dizinden gelindi. Yasal
 * metne footer'dan, kayıt formundan, bir e-postadan gelindi — geri dönülecek
 * tek bir yer yok.
 *
 * Buna karşılık bu dört belge birbirine ATIF YAPIYOR ("çerez ayrıntısı için
 * Çerez Politikası"). Okuyucuyu o cümleyi okuduktan sonra footer'a geri
 * göndermek, aradığı metni bulmasını zorlaştırırdı. Şerit bu yüzden dördünü
 * de her sayfanın başında tutuyor.
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * Hareket ne söylüyor (DESIGN.md §4)
 * ═══════════════════════════════════════════════════════════════════════════
 *   • Okuma çubuğu → "bu metnin neresindeyim". Uzun bir sözleşmede gerçekten
 *     sorulan soru; kaydırmanın kendisine bağlı, süs değil.
 *   • İçindekiler işareti → `layoutId` ile bölümden bölüme TAŞINIYOR:
 *     "aynı işaret, şimdi burada".
 *   • Başlık bloğu → okuma sırasına göre açılıyor (künye → başlık → özet →
 *     şerit). Sayfanın tepesinde olduğu için `whileInView` değil `animate`.
 *   • Gövde HAREKETSİZ → `initial={false}`. Blog yazısında kaydırdıkça açılan
 *     bloklar 9 Eyl 2026'da kullanıcı kararıyla kaldırılmıştı; bir sözleşmede
 *     zaten hiç doğru olmazdı — metin açıldığı an tamamı orada olmalı, hatta
 *     tarayıcının Ctrl+F'i ilk karede çalışmalı.
 */

/* Başlık bloğu — blog ile aynı set, aynı gerekçe. */
const headV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.base, delayChildren: 0.05 } },
};

const lineV: Variants = {
  hidden: { opacity: 0, y: 12 },
  show: { opacity: 1, y: 0, transition: springFade(spring.default) },
};

/* Sayfadaki tek blur: belgenin adı. */
const titleV: Variants = {
  hidden: { opacity: 0, y: 16, filter: 'blur(6px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: springFade(spring.default, dur.base),
  },
};

/* Şerit kalemleri sırayla oturuyor — soldan sağa, okuma yönünde. */
const stripV: Variants = {
  hidden: {},
  show: { transition: { staggerChildren: stagger.tight } },
};

const chipV: Variants = {
  hidden: { opacity: 0, y: 6 },
  show: { opacity: 1, y: 0, transition: springFade(spring.snap) },
};

/* İçindekiler metin tarafından (satırın sonundan) kayarak oturuyor; sağdan
   sola dilde sütun sağda, kayma soldan — işaret `custom` ile. */
const tocV: Variants = {
  hidden: (sign: number = 1) => ({ opacity: 0, x: 8 * sign }),
  show: { opacity: 1, x: 0, transition: { duration: dur.base, ease: ease.out } },
};

/**
 * Etkin başlığı bulmanın bandı — `blog-article.tsx` ile AYNI değer.
 *
 * Üstte ayrık header kabuğu kadar ölü alan, altta ekranın %65'i. Band olmadan
 * (`threshold` ile) iki başlık aynı anda görünürken işaret ikisi arasında
 * zıplıyor.
 */
const TOC_BAND = '-88px 0px -65% 0px';

export function LegalDoc({ doc }: { doc: LegalDocDetail }) {
  const t = useTranslations('legal');
  const locale = useLocale();
  const uid = useId();
  const sign = useInlineSign();

  const bodyRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<string>('');

  /* Çubuk GÖVDENİN boyuna bağlı, sayfanınkine değil: başlık bloğu ve şerit
     okuma değil. Kullanıcı son maddeyi okuduğunda çubuk dolu olmalı. */
  const { scrollYProgress } = useScroll({
    target: bodyRef,
    offset: ['start start', 'end end'],
  });

  /* `spring.snap`in sayıları — `type` alanı `useSpring`in beklediği yapıda
     yok. Ham sayı yazmak yerine token'dan sıyırmak DESIGN.md §5'in "değerler
     bileşenlerde tekrarlanmaz" kuralını koruyor. */
  const { type: _snapType, ...snapConfig } = spring.snap;
  const progress = useSpring(scrollYProgress, snapConfig);

  useEffect(() => {
    if (doc.toc.length === 0) return;

    const targets = doc.toc
      .map((entry) => document.getElementById(entry.anchor))
      .filter((el): el is HTMLElement => el !== null);

    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const inBand = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

        // Banda hiçbir başlık girmediğinde ETKİN OLANI BIRAKIYORUZ: son
        // bölümün altındaki paragrafları okurken işaretin sönmesi "artık
        // hiçbir bölümde değilim" demek olurdu.
        if (inBand[0]) setActive(inBand[0].target.id);
      },
      { rootMargin: TOC_BAND, threshold: 0 },
    );

    for (const target of targets) observer.observe(target);
    return () => observer.disconnect();
  }, [doc.toc]);

  /* Son güncelleme yürürlük gününden SONRAYSA ayrıca basılıyor.
     ---------------------------------------------------------------------
     🔴 Karşılaştırma DAMGAYLA değil, EKRANDA GÖRÜNEN DİZEYLE.
     İlk sürüm `new Date(updatedAt) > new Date(effectiveAt)` diyordu ve
     tohumun yazıldığı gün yakalandı: yürürlük 17 Eyl 00:00, kayıt 17 Eyl
     08:08 — koşul doğru, ekranda ise "Yürürlük 17 Eyl 2026 · Son güncelleme
     17 Eyl 2026" yazıyordu. Aynı günü iki kez söylemek, bir sözleşmede
     olmayan bir değişikliğe işaret eder.

     Görünen değer bir GÜN; kıyas da gün üzerinden olmalı. Biçimlendirilmiş
     dizeyi kıyaslamak ayrıca saat dilimi sorusunu da kapatıyor: kullanıcı ne
     görüyorsa o kıyaslanıyor. */
  /**
   * Metnin dilinin ADI, arayüzün dilinde ("Türkisch", "トルコ語").
   *
   * `Intl.DisplayNames` her dilde doğru adı biliyor; mesaj dosyasındaki
   * `locales.*` haritası yalnız tarayıcı API'yi desteklemezse yedek.
   */
  const languageLabel = (code: string): string => {
    try {
      return new Intl.DisplayNames([locale], { type: 'language' }).of(code) ?? code;
    } catch {
      return t.has(`locales.${code}`) ? t(`locales.${code}`) : code;
    }
  };

  /**
   * Metin MAKİNE ÇEVİRİSİ mi (29 Eyl 2026, kullanıcı kararı).
   *
   * `en` ve `tr` elle yazılmış asıl metinler; diğer on bir dildeki yasal
   * metin DeepL çevirisi. Bir sözleşmenin çevirisini asıl metin gibi sunmak
   * yanıltıcı olurdu: çevirinin başına "uyuşmazlıkta Türkçe asıl metin
   * esastır" notu ve iki asıl metne bağlantı basılıyor. Metin yedek dilden
   * geliyorsa (`fallbackLocale`) bu not değil aşağıdaki "çevrilmedi" notu
   * görünüyor — ikisi aynı anda anlamsız.
   */
  const machineTranslated = !doc.fallbackLocale && TRANSLATED_LOCALES.includes(doc.locale);

  const effectiveLabel = formatDate(doc.effectiveAt, locale);
  const updatedLabel = formatDate(doc.updatedAt, locale);
  const showUpdated = updatedLabel !== effectiveLabel;

  return (
    <article
      className="art"
      /* Metin istenen dilde yoksa (`fallbackLocale`) kap METNİN dilini ve
         yönünü taşıyor — gerekçe `blog-article.tsx`te aynı yerde. */
      {...(doc.locale !== locale ? { lang: doc.locale, dir: LOCALE_META[doc.locale].dir } : {})}
    >
      {/* `aria-hidden`: aynı bilgi ekran okuyucuya kaydırma konumuyla zaten
          iletiliyor; ikinci bir canlı bölge gürültü olurdu. */}
      <motion.div className="art-progress" style={{ scaleX: progress }} aria-hidden="true" />

      {/* Header `position: fixed` — akışta yer kaplamıyor. Pay `--header-h`ten
          değil ayrık kabuğun alt kenarından; `.page-head-space`, `.bhero-space`
          ve `.art-space` ile AYNI hesap. */}
      <div className="art-space" aria-hidden="true" />

      <div className="grid-12-container">
        <div className="grid-item-8fr">
          <div className="art-frame">
            <motion.div className="art-head" variants={headV} initial="hidden" animate="show">
              <motion.p className="art-meta" variants={lineV}>
                <span className="art-topic">{t('eyebrow')}</span>
                <span aria-hidden="true">·</span>
                <span>{t('version', { version: doc.version })}</span>
                <span aria-hidden="true">·</span>
                {/* `<time>`: tarih makine tarafından okunabilir olmalı —
                    bir sözleşmenin yürürlük günü, biçimlendirilmiş metinden
                    ibaret bir şey değil. */}
                <span>
                  {t('effective')} <time dateTime={doc.effectiveAt}>{effectiveLabel}</time>
                </span>
                {showUpdated ? (
                  <>
                    <span aria-hidden="true">·</span>
                    <span>
                      {t('updated')} <time dateTime={doc.updatedAt}>{updatedLabel}</time>
                    </span>
                  </>
                ) : null}
              </motion.p>

              <motion.h1 className="art-title" variants={titleV}>
                {doc.title}
              </motion.h1>

              <motion.p className="art-lede" variants={lineV}>
                {doc.lead}
              </motion.p>

              {/* Çeviri yoksa metin başka dilde geliyor ve bu SÖYLENİYOR.
                  Sessizce İngilizce bir sözleşme göstermek, hiç göstermemekten
                  kötü olurdu (`legal.service.ts` → `pickTranslation`). */}
              {doc.fallbackLocale ? (
                <motion.aside className="art-note legal-fallback" variants={lineV}>
                  <RiInformationLine className="art-note-icon" size={18} aria-hidden="true" />
                  <div className="art-note-text">
                    <p className="art-note-title">{t('fallback.title')}</p>
                    <p className="art-note-body">
                      {t('fallback.body', { locale: languageLabel(doc.locale) })}
                    </p>
                  </div>
                </motion.aside>
              ) : null}

              {machineTranslated ? (
                <motion.aside className="art-note legal-fallback" variants={lineV}>
                  <RiInformationLine className="art-note-icon" size={18} aria-hidden="true" />
                  <div className="art-note-text">
                    <p className="art-note-title">{t('translated.title')}</p>
                    <p className="art-note-body">
                      {t('translated.body')}{' '}
                      {t('translated.original')}{' '}
                      <Link href={`/${doc.slug}`} locale="tr" hrefLang="tr" lang="tr">
                        Türkçe
                      </Link>
                      {' · '}
                      <Link href={`/${doc.slug}`} locale="en" hrefLang="en" lang="en">
                        English
                      </Link>
                    </p>
                  </div>
                </motion.aside>
              ) : null}

              <motion.nav className="legal-strip" aria-label={t('navLabel')} variants={stripV}>
                <ul className="legal-strip-list">
                  {doc.siblings.map((item) => (
                    <motion.li key={item.slug} variants={chipV}>
                      {item.current ? (
                        /* Bulunduğu sayfaya BAĞLANMIYOR. Kendine giden bir
                           bağlantı, ekran okuyucuya "gidilecek bir yer"
                           olarak duyuruluyor ve tıklayan kullanıcı hiçbir
                           şey olmadığını görüyor. */
                        <span className="legal-strip-item" aria-current="page">
                          {item.title}
                        </span>
                      ) : (
                        <Link className="legal-strip-item" href={`/${item.slug}`}>
                          {item.title}
                        </Link>
                      )}
                    </motion.li>
                  ))}
                </ul>
              </motion.nav>
            </motion.div>

            <div className="art-layout">
              {/* Düz `div`: `motion.div` olsaydı varyantları çocuklara DAĞITIR
                  ve bloklar tek tek oynardı. Kabın tek işi ölçüm (okuma
                  çubuğu) ve düzen. */}
              <div className="art-body" ref={bodyRef}>
                {doc.body.map((block, index) => (
                  <Block key={index} block={block} scrollHint={t('tableScroll')} />
                ))}
              </div>

              {/* Yasal metinde içindekiler HER ZAMAN var: blogdaki "iki
                  başlıktan azsa gizle" eşiği burada yok (`legal.mappers.ts`).
                  Yine de koşul duruyor — gövdesi bozulmuş bir belge başlıksız
                  kalabilir ve boş bir kenar sütunu basmanın anlamı yok. */}
              {doc.toc.length > 0 ? (
                <motion.nav
                  className="art-toc"
                  aria-label={t('toc')}
                  variants={tocV}
                  custom={sign}
                  initial="hidden"
                  animate="show"
                >
                  <p className="art-toc-title">{t('toc')}</p>
                  <ol className="art-toc-list">
                    {doc.toc.map((entry) => {
                      const current = entry.anchor === active;

                      return (
                        <li className="art-toc-item" key={entry.anchor}>
                          <a
                            className="art-toc-link"
                            href={`#${entry.anchor}`}
                            aria-current={current ? 'true' : undefined}
                          >
                            {current ? (
                              <motion.span
                                className="art-toc-mark"
                                aria-hidden="true"
                                layoutId={`${uid}-legal-toc`}
                                transition={spring.default}
                              />
                            ) : null}
                            {/* Metin kendi katmanında: işaret `absolute` ve
                                sarmalayıcı olmadan metnin ÜSTÜNE binerdi. */}
                            <span className="art-toc-text">{entry.text}</span>
                          </a>
                        </li>
                      );
                    })}
                  </ol>
                </motion.nav>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/**
 * Tek blok.
 *
 * `switch` tam kapsayıcı: sözleşmeye yeni bir blok tipi eklendiğinde
 * TypeScript burada durur. Bilinçli — yeni bir tip, ekranda karşılığı olmadan
 * sözleşmeye giremesin.
 *
 * Bloklar `initial={false}` ile basılıyor: hareket YOK. Gerekçe bileşen
 * başlığında.
 */
function Block({ block, scrollHint }: { block: LegalBlock; scrollHint: string }) {
  switch (block.type) {
    case 'heading':
      return (
        // Çıpa SUNUCUDA türetiliyor (`legal.mappers.ts`) ve bir kez
        // yazıldıktan sonra korunuyor — burada yeniden hesaplansaydı
        // içindekilerdeki bağlantıdan ayrılabilirdi.
        <h2 className="art-h2" id={block.anchor}>
          {block.text}
        </h2>
      );

    case 'subheading':
      return <h3 className="art-h3">{block.text}</h3>;

    case 'paragraph':
      return <p className="art-p">{block.text}</p>;

    case 'list': {
      const items = block.items.map((item, index) => (
        <li className="art-li" key={index}>
          {item}
        </li>
      ));

      return block.ordered ? (
        <ol className="art-list art-list--ordered">{items}</ol>
      ) : (
        <ul className="art-list">{items}</ul>
      );
    }

    case 'note':
      /* İkon kutu İÇİNDE değil, doğrudan zemine oturuyor — DESIGN.md §3'ün
         kesinleşmiş kararı. Notun zemini ve sol hattı kabın kendisinde. */
      return (
        <aside className="art-note">
          <RiInformationLine className="art-note-icon" size={18} aria-hidden="true" />
          <div className="art-note-text">
            {block.title ? <p className="art-note-title">{block.title}</p> : null}
            <p className="art-note-body">{block.text}</p>
          </div>
        </aside>
      );

    case 'table':
      return <LegalTable block={block} scrollHint={scrollHint} />;
  }
}

/**
 * Tablo bloğu.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Kaydırma kabı KLAVYEYLE ERİŞİLEBİLİR olmak zorunda
 * ---------------------------------------------------------------------------
 * Dar ekranda tablo kendi kabında yatay kayıyor (sayfa gövdesi asla —
 * DESIGN.md §2). Ama `overflow: auto` taşıyan bir kutu, içinde odaklanabilir
 * bir öğe yoksa klavyeyle kaydırılamaz: fare tekerleği olmayan kullanıcı
 * tablonun sağ yarısını hiç göremez.
 *
 * Çözüm standart: kaba `tabindex="0"` ve bir `role="region"` + erişilebilir
 * ad. Böylece kap sekmeyle odaklanıyor, ok tuşlarıyla kayıyor ve ekran
 * okuyucu ona bir bölge olarak giriyor.
 *
 * `aria-label` `<caption>`dan geliyor; ikisi aynı cümle çünkü kabın adı da
 * tablonun ne anlattığı. Altyazı yoksa genel ipucu metni kullanılıyor —
 * adsız bir `region` ekran okuyucuda "region" diye duyurulur ve bu bilgi
 * değil gürültüdür.
 */
function LegalTable({
  block,
  scrollHint,
}: {
  block: Extract<LegalBlock, { type: 'table' }>;
  scrollHint: string;
}) {
  const label = block.caption ?? scrollHint;

  return (
    <div className="legal-table-wrap" role="region" aria-label={label} tabIndex={0}>
      <table className="legal-table">
        {block.caption ? <caption className="legal-table-cap">{block.caption}</caption> : null}
        <thead>
          <tr>
            {block.headers.map((cell, index) => (
              <th key={index} scope="col">
                {cell}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {block.rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, cellIndex) =>
                /* İlk sütun satırın BAŞLIĞI: "Çerez", "Amaç", "Veri
                   kategorisi" tablolarında satırın ne olduğunu o söylüyor.
                   `scope="row"` olmadan ekran okuyucu her hücreyi yalnızca
                   sütun başlığıyla okur ve hangi satırda olunduğu kaybolur. */
                cellIndex === 0 ? (
                  <th key={cellIndex} scope="row">
                    {cell}
                  </th>
                ) : (
                  <td key={cellIndex}>{cell}</td>
                ),
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
